import {FormEvent,useEffect,useRef,useState} from 'react';
import {ApiError,updateOwnDisplayName} from './api';

/**
 * "Signed in as" name with a self-service correction (Issue #697: the founder's header showed the
 * onboarding placeholder "Pending ProFenz owner"). Only the persisted name returned by the server
 * is ever shown as saved; input is kept on failure; an unknown outcome says so and asks for a
 * reload rather than claiming success or failure.
 */
export function OwnDisplayName({name,editable,onSaved}:{name:string;editable:boolean;onSaved:(name:string)=>void}){
  const [editing,setEditing]=useState(false),[draft,setDraft]=useState(name),[busy,setBusy]=useState(false);
  const [uncertain,setUncertain]=useState(false);
  const [message,setMessage]=useState<{kind:'saved'|'invalid'|'denied'|'unknown';text:string}|null>(null);
  const inputRef=useRef<HTMLInputElement>(null),statusRef=useRef<HTMLParagraphElement>(null),openerRef=useRef<HTMLButtonElement>(null);
  const submitting=useRef(false),generation=useRef(0),timer=useRef<ReturnType<typeof setTimeout>|undefined>(undefined);
  useEffect(()=>()=>{generation.current++;if(timer.current!==undefined)clearTimeout(timer.current);},[]);
  async function save(event:FormEvent){
    event.preventDefault();if(submitting.current||uncertain)return;
    const trimmed=draft.trim();
    if(trimmed===''){setMessage({kind:'invalid',text:'Enter your name.'});inputRef.current?.focus();return;}
    submitting.current=true;setBusy(true);setMessage(null);
    const observedGeneration=generation.current;
    try{
      // Bound observation of both fetch AND response-body parsing. The POST may
      // still commit after this deadline: never abort/retry it or call it failed.
      const saved=await Promise.race([
        updateOwnDisplayName(trimmed),
        new Promise<never>((_resolve,reject)=>{timer.current=setTimeout(()=>reject(new Error('Name save outcome is unknown.')),15000);}),
      ]);
      if(generation.current!==observedGeneration)return;
      submitting.current=false;
      onSaved(saved.display_name);setEditing(false);
      setMessage({kind:'saved',text:saved.changed?`Name saved as ${saved.display_name}.`:'Your name was already saved.'});
      requestAnimationFrame(()=>openerRef.current?.focus());
    }catch(error){
      if(generation.current!==observedGeneration)return;
      if(error instanceof ApiError&&error.status===400){submitting.current=false;setMessage({kind:'invalid',text:'Enter a name of 1 to 191 visible characters.'});}
      else {submitting.current=true;setUncertain(true);setMessage({kind:'unknown',text:"We couldn't confirm whether your name was saved. Do not submit it again. Check your current name in a freshly opened workspace; keep this page open if you have unsaved work."});}
      requestAnimationFrame(()=>statusRef.current?.focus());
    }finally{
      if(generation.current===observedGeneration){
        if(timer.current!==undefined){clearTimeout(timer.current);timer.current=undefined;}
        // Keep the synchronous lock latched on ambiguous outcomes as well as the
        // rendered disabled state. Only a definite 400/success can allow a retry.
        setBusy(false);
      }
    }
  }
  return <>
    <strong>{name}</strong>
    {editable&&!editing&&<> <button ref={openerRef} type="button" className="button-link" onClick={()=>{setDraft(name);setMessage(null);setEditing(true);requestAnimationFrame(()=>inputRef.current?.focus());}}>Change name</button></>}
    {editing&&<form className="own-display-name" onSubmit={save} aria-label="Change your display name">
      <label htmlFor="own-display-name-input">Your name</label>
      <input id="own-display-name-input" ref={inputRef} value={draft} maxLength={191} autoComplete="name" disabled={busy||uncertain} onChange={event=>setDraft(event.target.value)} aria-invalid={message?.kind==='invalid'?'true':undefined}/>
      <button type="submit" disabled={busy||uncertain}>{busy?'Saving…':'Save name'}</button>
      <button type="button" disabled={busy||uncertain} onClick={()=>{setEditing(false);setMessage(null);requestAnimationFrame(()=>openerRef.current?.focus());}}>Cancel</button>
    </form>}
    {message&&<p ref={statusRef} tabIndex={-1} className={message.kind==='saved'?'notice':'notice error'} role={message.kind==='saved'?'status':'alert'}>{message.text}</p>}
  </>;
}
